import { Box, Skeleton } from '@mui/material';
import {
  cardGridSx,
  cardSurfaceSx,
  gridGapSx,
  searchColumnsSx,
  sectionSpacingSx,
  tokens,
} from '../../theme';
import type { LoadingSkeletonProps, SkeletonLayout } from './types';

/**
 * The shape of the page, in grey, while it loads. **One layout per route, which
 * is maintenance debt taken knowingly** — see
 * `docs/adr/0004-per-route-skeletons.md`.
 *
 * **One live region for the whole thing.** The bars are `aria-hidden` and the
 * container says "Loading <what>" once; twenty announced placeholders is worse
 * than silence.
 */

/**
 * The lifecycle's shape, hardcoded — a placeholder for data that has not arrived
 * cannot be derived from it, so a taxonomy change is a thing to come and change
 * here and in the rail's skeleton.
 */
const STAGE_KEYS = ['discover', 'design', 'build', 'run'] as const;
const LIFECYCLE_STAGE_CARDS = [1, 3, 3, 3];
const LIFECYCLE_ROWS = 3;

/** A page heading and its standfirst. */
const HeaderBars = ({ width = '35%' }: { width?: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Skeleton variant="text" width={width} height={44} />
    <Skeleton variant="text" width="55%" height={20} />
  </Box>
);

/** A card: a short reference, a title and a few lines. */
const CardBlock = ({ lines = 3 }: { lines?: number }) => (
  <Box
    sx={{
      ...cardSurfaceSx,
      padding: 6,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      // Fills whatever row it is given, so a column holding one card matches a
      // column holding three rather than leaving a field of white beneath it.
      height: '100%',
    }}
  >
    <Skeleton variant="text" width="30%" height={14} />
    <Skeleton variant="text" width="70%" height={22} />
    {Array.from({ length: lines }, (_, index) => (
      <Skeleton key={index} variant="text" width={index === lines - 1 ? '60%' : '100%'} height={14} />
    ))}
  </Box>
);

/** A prose section: heading, caption, and a paragraph of it. */
const ProseBlock = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Skeleton variant="text" width="18%" height={26} />
    <Skeleton variant="text" width="30%" height={16} />
    <Box sx={{ marginTop: 2 }}>
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="97%" height={16} />
      <Skeleton variant="text" width="64%" height={16} />
    </Box>
  </Box>
);

/**
 * The L1 grid: four stage headers over their columns of cards. **The headers are
 * drawn too** — they are the widest, greyest thing on the page, so leaving them
 * out makes the skeleton the wrong shape entirely.
 */
const LifecycleSkeleton = () => (
  <>
    <HeaderBars width="40%" />
    <Box sx={{ ...cardGridSx('stage'), ...sectionSpacingSx }}>
      {LIFECYCLE_STAGE_CARDS.map((cards, column) => (
        <Box
          key={STAGE_KEYS[column]}
          sx={{ display: 'flex', flexDirection: 'column', ...gridGapSx }}
        >
          <Skeleton
            variant="rounded"
            height={41}
            sx={{ borderRadius: `${String(tokens.radius.md)}px` }}
          />

          {/*
            The same equal-row grid the real columns use, so Discover's single
            card spans all three rows rather than sitting atop an empty column.
          */}
          <Box
            sx={{
              flex: 1,
              display: 'grid',
              gridTemplateRows: `repeat(${String(LIFECYCLE_ROWS)}, 1fr)`,
              ...gridGapSx,
            }}
          >
            {Array.from({ length: cards }, (_, card) => (
              <Box
                key={`${STAGE_KEYS[column] ?? ''}-${String(card)}`}
                sx={{ gridRow: `span ${String(LIFECYCLE_ROWS / cards)}` }}
              >
                <CardBlock />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  </>
);

/** Phase and process pages: a header, prose sections, two ICOM columns, a grid. */
const DetailSkeleton = ({ cards }: { cards: number }) => (
  <>
    <HeaderBars />
    <Box sx={{ ...sectionSpacingSx, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <ProseBlock />
      <ProseBlock />
    </Box>
    <Box
      sx={{
        ...sectionSpacingSx,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        ...gridGapSx,
      }}
    >
      {Array.from({ length: 4 }, (_, index) => (
        <CardBlock key={index} lines={4} />
      ))}
    </Box>
    <Box sx={{ ...cardGridSx('card'), ...sectionSpacingSx }}>
      {Array.from({ length: cards }, (_, index) => (
        <CardBlock key={index} />
      ))}
    </Box>
  </>
);

/** The method page: the 936/400 two-column body, then the blocks below it. */
const MethodSkeleton = () => (
  <>
    <HeaderBars />
    <Box
      sx={{
        ...sectionSpacingSx,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 936fr) minmax(0, 400fr)' },
        columnGap: 8,
        rowGap: 6,
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <ProseBlock />
        <ProseBlock />
        <ProseBlock />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <CardBlock lines={5} />
        <CardBlock lines={3} />
      </Box>
    </Box>
    <Box sx={{ ...sectionSpacingSx, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, ...gridGapSx }}>
      {Array.from({ length: 3 }, (_, index) => (
        <CardBlock key={index} lines={3} />
      ))}
    </Box>
  </>
);

/**
 * The search page. **Result cards are drawn wide and short rather than as grid
 * cards**, because the page delivers a column of full-width rows. Five is one
 * screenful; the real count is unknowable before the search runs.
 */
const SEARCH_RESULT_ROWS = 5;
const SEARCH_FILTER_ROWS = 8;

const SearchSkeleton = () => (
  <>
    <HeaderBars width="30%" />
    {/* Same named areas and same source order as the page, so the placeholder
        does not swap sides with the thing it stands in for. */}
    <Box sx={{ ...searchColumnsSx, ...sectionSpacingSx }}>
      <Box sx={{ ...cardSurfaceSx, gridArea: 'filters', padding: 6 }}>
        <Skeleton variant="text" width="40%" height={26} />
        <Box sx={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: SEARCH_FILTER_ROWS }, (_, index) => (
            <Skeleton key={index} variant="text" width="100%" height={16} />
          ))}
        </Box>
      </Box>

      <Box sx={{ gridArea: 'results', display: 'flex', flexDirection: 'column', ...gridGapSx }}>
        {Array.from({ length: SEARCH_RESULT_ROWS }, (_, index) => (
          <Box key={index} sx={{ ...cardSurfaceSx, padding: 6 }}>
            <Skeleton variant="text" width="22%" height={14} />
            <Skeleton variant="text" width="45%" height={26} />
            <Skeleton variant="text" width="60%" height={14} />
            <Skeleton variant="text" width="90%" height={14} />
          </Box>
        ))}
      </Box>
    </Box>
  </>
);

/** The agent library: a header over a grid of cards. */
const AgentsSkeleton = () => (
  <>
    <HeaderBars width="30%" />
    <Box sx={{ ...cardGridSx('card'), ...sectionSpacingSx }}>
      {Array.from({ length: 3 }, (_, index) => (
        <CardBlock key={index} lines={3} />
      ))}
    </Box>
  </>
);

/**
 * An agent's page: a header, then the prompt on the left and the version list and
 * the methods beside it, in the same two columns the page draws.
 */
const AgentSkeleton = () => (
  <>
    <HeaderBars width="30%" />
    <Box
      sx={{
        ...sectionSpacingSx,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 936fr) minmax(0, 400fr)' },
        columnGap: 8,
        rowGap: 6,
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Skeleton
          variant="rounded"
          height={280}
          sx={{ borderRadius: `${String(tokens.radius.md)}px` }}
        />
        <ProseBlock />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <CardBlock lines={4} />
        <CardBlock lines={3} />
      </Box>
    </Box>
  </>
);

const LAYOUTS: Record<SkeletonLayout, () => React.ReactElement> = {
  lifecycle: LifecycleSkeleton,
  phase: () => <DetailSkeleton cards={6} />,
  process: () => <DetailSkeleton cards={3} />,
  method: MethodSkeleton,
  search: SearchSkeleton,
  agents: AgentsSkeleton,
  agent: AgentSkeleton,
};

export const LoadingSkeleton = ({ layout, label }: LoadingSkeletonProps) => {
  const Layout = LAYOUTS[layout];

  return (
    <Box aria-busy aria-live="polite" aria-label={label}>
      <Box aria-hidden>
        <Layout />
      </Box>
    </Box>
  );
};
